const BACKEND_URI = "";

import {
    ChatRequest,
    HistoryRequest,
    Conversation,
    ChatMessage,
    CosmosDBHealth,
    CosmosDBStatus,
    Assistant,
    SendEmailRequest,
    ApiResponse,
    AuthClaims,
    UpdateTitleResponse,
    DeleteConversationResponse,
    VideoEmbedTokenResponse,
    AssistantFile,
    SearchEntity,
    RawConversation,
    RawChatMessage,
    RawAssistant,
    CreateAssistantResponse,
    CheckGroupsResponse,
    UserConfig,
    ChatUploadLimits
} from "./models";
import { acquireToken, getActiveAuthType } from "../authConfig";
import { notifyAuthRequired } from "../auth/authEvents";

export class AuthRequiredError extends Error {
    constructor(message = "Your session has expired. Please sign back in.") {
        super(message);
        this.name = "AuthRequiredError";
    }
}

function withAuth(init: RequestInit, accessToken: string): RequestInit {
    const headers: Record<string, string> = {
        ...(init.headers as Record<string, string> | undefined),
        Authorization: `Bearer ${accessToken}`
    };
    const authType = getActiveAuthType();
    if (authType) {
        headers["X-Auth-Type"] = authType;
    }
    return { ...init, headers };
}

// All backend calls go through here: acquires a token per request (MSAL caches
// and silently refreshes), retries a 401 once with a force-refreshed token, and
// throws AuthRequiredError (after notifying the session-expired modal) only when
// interactive re-auth is genuinely required. Network/other failures throw the
// raw error so they never masquerade as session expiry.
export async function authedFetch(input: string, init: RequestInit = {}): Promise<Response> {
    const acquired = await acquireToken();
    if (acquired.kind === "interaction-required") {
        notifyAuthRequired();
        throw new AuthRequiredError();
    }
    if (acquired.kind === "failure") {
        throw acquired.error;
    }

    const response = await fetch(input, withAuth(init, acquired.result.accessToken));
    if (response.status !== 401) {
        return response;
    }

    const refreshed = await acquireToken({ forceRefresh: true });
    if (refreshed.kind === "failure") {
        throw refreshed.error;
    }
    if (refreshed.kind === "success") {
        const retried = await fetch(input, withAuth(init, refreshed.result.accessToken));
        if (retried.status !== 401) {
            return retried;
        }
    }
    notifyAuthRequired();
    throw new AuthRequiredError();
}

const JSON_HEADERS: Record<string, string> = { "Content-Type": "application/json" };

export async function chatApi(request: ChatRequest, signal?: AbortSignal): Promise<Response> {
    return await authedFetch(`${BACKEND_URI}/chat`, {
        method: "POST",
        headers: JSON_HEADERS,
        body: JSON.stringify(request),
        signal
    });
}

function getCitationFilePath(citation: string, assistant_type: string, assistant_id: string | undefined, idToken: string | undefined): string {
    // If .mp4, wait for video indexer access token
    if (citation.includes('.mp4')) {
        return citation;
    }

    const finalCitation = assistant_type === 'custom'
        ? `${assistant_id}/${citation}`
        : citation

    const url = `${BACKEND_URI}/content/${finalCitation}`
    const authType = getActiveAuthType() || "azuread";
    const tokenParam = idToken ? `?bt=${idToken}&auth_type=${authType}` : '';

    const hashIndex = url.indexOf('#')
    if (hashIndex > -1) {
        const baseUrl = url.substring(0, hashIndex)
        const hashPart = url.substring(hashIndex)
        return `${baseUrl}${tokenParam}${hashPart}`
    }
    return `${url}${tokenParam}`
}

// Builds a /content citation URL with a freshly acquired token, so links built
// long after page load don't embed an expired one.
export async function getFreshCitationUrl(citation: string, assistantType: string, assistantId?: string): Promise<string> {
    if (citation.includes('.mp4')) {
        return citation;
    }

    const acquired = await acquireToken();
    if (acquired.kind === "interaction-required") {
        notifyAuthRequired();
        throw new AuthRequiredError();
    }
    if (acquired.kind === "failure") {
        throw acquired.error;
    }
    return getCitationFilePath(citation, assistantType, assistantId, acquired.result.accessToken);
}

// Fetches a code-interpreter-generated file with header auth (authedFetch
// acquires a fresh token per request), so no tokens ever land in URLs and
// expired blobs surface as a thrown error instead of a downloaded error page.
export async function fetchGeneratedFile(blobPath: string, download = false): Promise<Blob> {
    const suffix = download ? "?download=1" : "";
    const response = await authedFetch(`${BACKEND_URI}/generated-file/${blobPath}${suffix}`);
    if (!response.ok) {
        throw new Error(`Failed to fetch generated file (${response.status})`);
    }
    return response.blob();
}

export function buildVideoEmbedUrl(accountId: string, videoId: string, accessToken: string, location: string, startTimeSeconds?: number): string {
    let url = `https://www.videoindexer.ai/embed/player/${accountId}/${videoId}/?accessToken=${encodeURIComponent(accessToken)}&locale=en&location=${location}`;
    if (startTimeSeconds !== undefined) {
        url += `&t=${startTimeSeconds}`;
    }
    return url;
}

export async function getVideoEmbedToken(videoId: string): Promise<VideoEmbedTokenResponse> {
    const response = await authedFetch(`/api/video-embed-token/${videoId}`);
    if (!response.ok) {
        throw new Error("Failed to get video embed token");
    }
    return response.json();
}

export function parseVideoCitation(citation: string): { accountId: string; videoId: string; startTime?: number } | null {
    const parts = citation.split('?url=');
    if (parts.length < 2) return null;

    const urlPart = parts[1];
    const [accountAndVideo, timeParam] = urlPart.split('&t=');
    const [accountId, videoId] = accountAndVideo.split('/');

    return {
        accountId,
        videoId,
        startTime: timeParam ? parseFloat(timeParam) : undefined
    };
}

export async function getDocumentText(formData: FormData): Promise<Response> {
    return await authedFetch(`${BACKEND_URI}/document/get_text`, {
        method: "POST",
        body: formData
    });
}

export async function generateHistory(request: HistoryRequest): Promise<Response> {
    return await authedFetch(`${BACKEND_URI}/history/generate`, {
        method: "POST",
        headers: JSON_HEADERS,
        body: JSON.stringify(request)
    });
}

export const historyCount = async (assistantId?: string): Promise<number> => {
    const response = await authedFetch("/history/count", {
        method: "POST",
        headers: JSON_HEADERS,
        body: JSON.stringify({
            assistantId: assistantId
        }),
    })
        .then(async res => {
            if (!res.ok) {
                return 0;
            }
            const payload = await res.json();
            let count: number = 0;
            if (payload?.conversation_count) {
                count = payload.conversation_count;
            }
            return count;
        })
        .catch(err => {
            if (err instanceof AuthRequiredError) throw err;
            console.error("There was an issue fetching your data.");
            return 0;
        });
    return response;
};

export const historyList = async (offset?: number, assistantId?: string): Promise<Conversation[] | null> => {
    const response = await authedFetch("/history/list", {
        method: "POST",
        headers: JSON_HEADERS,
        body: JSON.stringify({
            offset: offset,
            assistantId: assistantId
        }),
    })
        .then(async res => {
            const payload = await res.json();
            if (!Array.isArray(payload)) {
                console.error("There was an issue fetching your data.");
                return null;
            }
            const conversations: Conversation[] = await Promise.all(
                payload.map(async (conv: RawConversation) => {
                    let convMessages: ChatMessage[] = [];
                    convMessages = await historyRead(conv.id)
                        .then(res => {
                            return res;
                        })
                        .catch(err => {
                            if (err instanceof AuthRequiredError) throw err;
                            console.error("error fetching messages: ", err);
                            return [];
                        });
                    const conversation: Conversation = {
                        id: conv.id,
                        assistant: conv.assistant,
                        assistant_id: conv.assistantId,
                        title: conv.title,
                        messages: convMessages,
                        date: conv.updatedAt,
                        pinned: conv.pinned || false
                    };
                    return conversation;
                })
            );
            return conversations;
        })
        .catch(err => {
            if (err instanceof AuthRequiredError) throw err;
            console.error("There was an issue fetching your data.");
            return null;
        });

    return response;
};

export const historyRead = async (convId: string): Promise<ChatMessage[]> => {
    const response = await authedFetch("/history/read", {
        method: "POST",
        body: JSON.stringify({
            conversation_id: convId
        }),
        headers: JSON_HEADERS
    })
        .then(async res => {
            const payload = await res.json();
            let messages: ChatMessage[] = [];
            if (payload?.chat_messages) {
                payload.chat_messages.forEach((msg: RawChatMessage) => {
                    const message: ChatMessage = {
                        id: msg.id,
                        message: msg.message,
                        context: msg.context,
                        date: msg.createdAt
                    };
                    messages.push(message);
                });
            }
            return messages;
        })
        .catch(err => {
            if (err instanceof AuthRequiredError) throw err;
            console.error("There was an issue fetching your data.");
            return [];
        });
    return response;
};

export const historyEnsure = async (): Promise<CosmosDBHealth> => {
    const response = await authedFetch("/history/ensure", {
        method: "GET",
        headers: JSON_HEADERS
    })
        .then(async res => {
            let respJson = await res.json();
            let formattedResponse;
            if (respJson.message) {
                formattedResponse = CosmosDBStatus.Working;
            } else {
                if (res.status === 500) {
                    formattedResponse = CosmosDBStatus.NotWorking;
                } else {
                    formattedResponse = CosmosDBStatus.NotConfigured;
                }
            }
            if (!res.ok) {
                return {
                    cosmosDB: false,
                    status: formattedResponse
                };
            } else {
                return {
                    cosmosDB: true,
                    status: formattedResponse
                };
            }
        })
        .catch(err => {
            if (err instanceof AuthRequiredError) throw err;
            console.error("There was an issue fetching your data.");
            return {
                cosmosDB: false,
                status: err
            };
        });
    return response;
};

export const assistantsEnsure = async (): Promise<CosmosDBHealth> => {
    const response = await authedFetch("/assistants/ensure", {
        method: "GET",
        headers: JSON_HEADERS
    })
        .then(async res => {
            let respJson = await res.json();
            let formattedResponse;
            if (respJson.message) {
                formattedResponse = CosmosDBStatus.Working;
            } else {
                if (res.status === 500) {
                    formattedResponse = CosmosDBStatus.NotWorking;
                } else {
                    formattedResponse = CosmosDBStatus.NotConfigured;
                }
            }
            if (!res.ok) {
                return {
                    cosmosDB: false,
                    status: formattedResponse
                };
            } else {
                return {
                    cosmosDB: true,
                    status: formattedResponse
                };
            }
        })
        .catch(err => {
            if (err instanceof AuthRequiredError) throw err;
            console.error("There was an issue fetching your assistants.");
            return {
                cosmosDB: false,
                status: err
            };
        });
    return response;
};

export const assistantsList = async (): Promise<Assistant[] | null> => {
    const response = await authedFetch("/assistants/list", {
        method: "GET",
        headers: JSON_HEADERS
    })
        .then(async res => {
            const payload = await res.json();
            if (!Array.isArray(payload)) {
                console.error("There was an issue fetching your assistants.");
                return null;
            }
            const assistants: Assistant[] = await Promise.all(
                payload.map(async (assist: RawAssistant) => {
                    const assistant: Assistant = {
                        id: assist.id,
                        created_by: assist.created_by,
                        user_display_name: assist.user_display_name,
                        created_at: assist.created_at,
                        assistant: assist.assistant,
                        title: assist.title,
                        system_prompt_message: assist.system_prompt_message,
                        welcome_message: assist.welcome_message,
                        files: assist.files,
                        examples: assist.examples,
                        type: assist.type,
                        short_title: assist.short_title,
                        url: assist.url,
                        attachments: assist.attachments,
                        tools: assist.tools,
                        tool_defaults: assist.tool_defaults,
                        userIds: assist.userIds,
                        groups: assist.groups,
                        managerIds: assist.managerIds ?? [],
                        managerGroups: assist.managerGroups ?? [],
                        can_edit: assist.can_edit === true,
                        config: assist.config,
                        available: assist.available
                    };
                    return assistant;
                })
            );
            return assistants;
        })
        .catch(err => {
            if (err instanceof AuthRequiredError) throw err;
            console.error("There was an issue fetching your assistants.");
            return null;
        });

    return response;
};

export async function sendSupportEmail(request: SendEmailRequest): Promise<{ message: string } | null> {
    const response = await authedFetch(`${BACKEND_URI}/training/support_email`, {
        method: 'POST',
        headers: JSON_HEADERS,
        body: JSON.stringify(request)
    })
        .then(async res => {
            if (!res.ok) {
                console.error("There was an issue sending your email.");
                return null;
            }
            const payload = await res.json();
            return payload.message;
        })
        .catch(err => {
            if (err instanceof AuthRequiredError) throw err;
            console.error("There was an issue sending your email.");
            return null;
        });
    return response;
}

export async function submitCreateAssistant(title: string, assistantUrl: string, promptMessage: string, welcomeMessage: string, selectedUserIds: string[], selectedGroups: string[], tools: string[], toolDefaults: string[], selectedManagerIds: string[], selectedManagerGroupIds: string[]): Promise<{ ok: boolean; data: CreateAssistantResponse }> {
    const formData = new FormData();
    formData.append('title', title);
    formData.append('assistantUrl', assistantUrl);
    formData.append('promptMessage', promptMessage);
    formData.append('welcomeMessage', welcomeMessage);
    selectedUserIds.forEach((userId) => formData.append('users', userId));
    selectedGroups.forEach((group) => formData.append('groups', group));
    tools.forEach((tool) => formData.append('tools', tool));
    toolDefaults.forEach((tool) => formData.append('tool_defaults', tool));
    selectedManagerIds.forEach((managerId) => formData.append('managers', managerId));
    selectedManagerGroupIds.forEach((groupId) => formData.append('managerGroups', groupId));

    const response = await authedFetch(`${BACKEND_URI}/assistant/submit`, {
        method: 'POST',
        body: formData,
    });

    const data = await response.json().catch(() => ({}));
    return { ok: response.ok, data };
}

export async function submitAssistantDocument(assistantId: string, file: File): Promise<ApiResponse<{ file_id: string; filename: string; status: string; error?: string }>> {
    const formData = new FormData();
    formData.append('file', file);

    const res = await authedFetch(`${BACKEND_URI}/upload-document/${assistantId}`, {
        method: 'POST',
        body: formData
    });

    const data = await res.json();
    return { ok: res.ok, status: res.status, data };
}

export async function submitAssistantVideo(
    assistantId: string,
    file: File
): Promise<ApiResponse<{ video_id: string; filename: string; status: string }>> {
    const formData = new FormData();
    formData.append('file', file);

    const res = await authedFetch(`${BACKEND_URI}/upload-video/${assistantId}`, {
        method: 'POST',
        body: formData
    });

    const data = await res.json();
    return { ok: res.ok, status: res.status, data };
}

export async function checkVideoStatus(assistantId: string, videoId: string): Promise<ApiResponse<{ status: string; video_id?: string }>> {
    const res = await authedFetch(`${BACKEND_URI}/video-status/${assistantId}/${videoId}`, {
        headers: JSON_HEADERS
    });

    const data = await res.json();
    return { ok: res.ok, status: res.status, data };
}

export async function finaliseVideo(assistantId: string, videoId: string, filename: string, fileSize: number): Promise<ApiResponse<{ status: string }>> {
    const res = await authedFetch(`${BACKEND_URI}/finalise-video/${assistantId}/${videoId}`, {
        method: 'POST',
        headers: JSON_HEADERS,
        body: JSON.stringify({
            filename,
            fileSize
        })
    });

    const data = await res.json();
    return { ok: res.ok, status: res.status, data };
}

export async function deleteAssistantFiles(assistantId: string, files: AssistantFile[]): Promise<Response> {
    const payload = {
        files: files.map(file => ({
            fileId: file.fileId,
            fileName: file.fileName,
            fileType: file.fileType
        }))
    };

    const response = await authedFetch(`${BACKEND_URI}/delete-files/${assistantId}`, {
        method: 'POST',
        headers: JSON_HEADERS,
        body: JSON.stringify(payload)
    });

    return response;
}

export async function submitEditAssistant(assistantId: string, title: string, assistantUrl: string, promptMessage: string, welcomeMessage: string, selectedUserIds: string[], selectedGroupIds: string[], tools: string[], toolDefaults: string[], selectedManagerIds: string[], selectedManagerGroupIds: string[]): Promise<Response> {
    const formData = new FormData()
    if (title) formData.append('title', title)
    if (assistantUrl) formData.append('assistantUrl', assistantUrl)
    if (promptMessage) formData.append('promptMessage', promptMessage)
    if (welcomeMessage) formData.append('welcomeMessage', welcomeMessage)

    selectedUserIds.forEach(id => formData.append('users', id))

    if (selectedGroupIds.length)
        selectedGroupIds.forEach(id => formData.append('groups', id))
    else
        formData.append('groups', '')

    if (tools.length)
        tools.forEach(tool => formData.append('tools', tool))
    else
        formData.append('tools', '')

    if (toolDefaults.length)
        toolDefaults.forEach(tool => formData.append('tool_defaults', tool))
    else
        formData.append('tool_defaults', '')

    if (selectedManagerIds.length)
        selectedManagerIds.forEach(id => formData.append('managers', id))
    else
        formData.append('managers', '')

    if (selectedManagerGroupIds.length)
        selectedManagerGroupIds.forEach(id => formData.append('managerGroups', id))
    else
        formData.append('managerGroups', '')

    return authedFetch(`${BACKEND_URI}/assistant/edit/${assistantId}`, {
        method: 'PUT',
        body: formData
    })
}

export async function enableAssistant(assistantId: string): Promise<Response> {
    const formData = new FormData();
    if (assistantId) {
        formData.append('assistantId', assistantId);
    }

    const response = await authedFetch(`${BACKEND_URI}/assistant/enable`, {
        method: 'PUT',
        body: formData
    });

    return response;
}

export async function disableAssistant(assistantId: string): Promise<Response> {
    const formData = new FormData();
    if (assistantId) {
        formData.append('assistantId', assistantId);
    }

    const response = await authedFetch(`${BACKEND_URI}/assistant/disable`, {
        method: 'PUT',
        body: formData
    });

    return response;
}

export async function leaveAssistant(assistantId: string): Promise<Response> {
    const response = await authedFetch(`${BACKEND_URI}/assistant/leave/${assistantId}`, {
        method: 'PATCH',
        body: ""
    });

    return response;
}

export async function deleteCustomAssistant(id: string): Promise<Response> {
    return await authedFetch(`${BACKEND_URI}/assistant/delete/${id}`, {
        method: 'DELETE'
    });
}

export async function checkGroups(): Promise<CheckGroupsResponse> {
    const fallback: CheckGroupsResponse = {
        can_create_assistants: false,
        is_assistant_builder_manager: false,
        assistant_builder_manager_group: null
    };

    const response = await authedFetch(`${BACKEND_URI}/check-groups`, {
        method: 'GET',
        headers: JSON_HEADERS
    });

    if (!response.ok) {
        console.error("Failed to check groups:", response.statusText);
        return fallback;
    }

    const data = await response.json();
    return {
        can_create_assistants: data.can_create_assistants === true,
        is_assistant_builder_manager: data.is_assistant_builder_manager === true,
        assistant_builder_manager_group: data.assistant_builder_manager_group ?? null
    };
}

export async function getChatUploadLimits(): Promise<ChatUploadLimits | null> {
    try {
        const response = await authedFetch(`${BACKEND_URI}/chat_limits`, {
            method: "GET",
            headers: JSON_HEADERS
        });

        if (!response.ok) {
            console.error("Failed to fetch chat upload limits:", response.statusText);
            return null;
        }

        return await response.json();
    } catch (error) {
        if (error instanceof AuthRequiredError) throw error;
        console.error("Error fetching chat upload limits:", error);
        return null;
    }
}

// Backend error bodies are `{ error: string }`; platform errors (413, 408, 500) are HTML.
export async function readErrorMessage(response: Response, fallback: string): Promise<string> {
    const body: { error?: string } | null = await response.json().catch(() => null);
    return body?.error ?? fallback;
}

type CacheEntry = { ts: number, data: SearchEntity[] }
const cache = new Map<string, CacheEntry>()
const TTL = 300000
let ctrl: AbortController | undefined

export async function searchEntities(q: string, includeB2c: boolean = false): Promise<SearchEntity[]> {
    if (!q.trim()) return []

    const cacheKey = `${q}|${includeB2c}`
    const c = cache.get(cacheKey)
    if (c && Date.now() - c.ts < TTL) return c.data

    ctrl?.abort()
    ctrl = new AbortController()

    try {
        const params = new URLSearchParams({
            q: q,
            include_b2c: includeB2c.toString()
        })

        const r = await authedFetch(`${BACKEND_URI}/search?${params}`, {
            method: 'GET',
            signal: ctrl.signal,
            headers: { Accept: 'application/json' }
        })

        if (!r.ok) throw new Error(`search failed ${r.status}`)

        const d = await r.json() as SearchEntity[]
        cache.set(cacheKey, { ts: Date.now(), data: d })
        return d
    } catch (e: any) {
        if (e.name === 'AbortError') return []
        throw e
    }
}

const resolveCache = new Map<string, CacheEntry>()
const RES_TTL = 300000
let resCtrl: AbortController | undefined

export async function resolveEntities(userIds: string[], groupIds: string[]): Promise<SearchEntity[]> {
    if (userIds.length === 0 && groupIds.length === 0) return []

    const key = [...userIds].sort().join(",") + "|" + [...groupIds].sort().join(",")
    const c = resolveCache.get(key)
    if (c && Date.now() - c.ts < RES_TTL) return c.data

    resCtrl?.abort()
    resCtrl = new AbortController()

    try {
        const r = await authedFetch(`${BACKEND_URI}/entities/resolve`, {
            method: "POST",
            signal: resCtrl.signal,
            headers: {
                ...JSON_HEADERS,
                Accept: "application/json"
            },
            body: JSON.stringify({ userIds, groupIds })
        })

        if (!r.ok) throw new Error(`resolve failed ${r.status}`)

        const d = (await r.json()) as SearchEntity[]
        resolveCache.set(key, { ts: Date.now(), data: d })
        return d
    } catch (e: any) {
        if (e.name === "AbortError") return []
        throw e
    }
}

export async function getAuthClaims(): Promise<AuthClaims | null> {
    try {
        const response = await authedFetch(`${BACKEND_URI}/get-auth-claims`, {
            method: 'GET',
            headers: JSON_HEADERS
        });

        if (!response.ok) {
            console.error("Failed to fetch auth claims:", response.statusText);
            return null;
        }

        const data = await response.json();
        return data;
    } catch (error) {
        if (error instanceof AuthRequiredError) throw error;
        console.error("Error fetching auth claims:", error);
        return null;
    }
}

export async function getUserConfig(): Promise<UserConfig | null> {
    try {
        const response = await authedFetch(`${BACKEND_URI}/user/config`, {
            method: "GET",
            headers: JSON_HEADERS
        });

        if (!response.ok) {
            console.error("Failed to fetch user config:", response.statusText);
            return null;
        }

        return await response.json();
    } catch (error) {
        if (error instanceof AuthRequiredError) throw error;
        console.error("Error fetching user config:", error);
        return null;
    }
}

export async function saveUserConfig(request: UserConfig): Promise<UserConfig | null> {
    try {
        const response = await authedFetch(`${BACKEND_URI}/user/config`, {
            method: "POST",
            headers: JSON_HEADERS,
            body: JSON.stringify(request)
        });

        if (!response.ok) {
            console.error("Failed to save user config:", response.statusText);
            return null;
        }

        return await response.json();
    } catch (error) {
        if (error instanceof AuthRequiredError) throw error;
        console.error("Error saving user config:", error);
        return null;
    }
}

export async function renameConversation(conversationId: string, newTitle: string): Promise<UpdateTitleResponse> {
    const payload = {
        conversation_id: conversationId,
        title: newTitle
    };
    const response = await authedFetch(`${BACKEND_URI}/history/rename`, {
        method: "PATCH",
        headers: JSON_HEADERS,
        body: JSON.stringify(payload)
    });
    if (!response.ok) throw new Error(`Failed to rename conversation: ${response.statusText}`);
    return response.json();
}

export async function deleteConversation(conversationId: string): Promise<DeleteConversationResponse> {
    const payload = {
        conversation_id: conversationId
    };
    const response = await authedFetch(`${BACKEND_URI}/history/delete`, {
        method: "DELETE",
        headers: JSON_HEADERS,
        body: JSON.stringify(payload)
    });
    if (!response.ok) throw new Error(`Failed to delete conversation: ${response.statusText}`);
    return response.json();
}

export async function pinConversation(conversationId: string): Promise<any> {
    const payload = {
        conversation_id: conversationId
    };
    const response = await authedFetch(`${BACKEND_URI}/history/pin`, {
        method: "PATCH",
        headers: JSON_HEADERS,
        body: JSON.stringify(payload)
    });
    if (!response.ok) throw new Error(`Failed to pin conversation: ${response.statusText}`);
    return response.json();
}

export async function unpinConversation(conversationId: string): Promise<any> {
    const payload = {
        conversation_id: conversationId
    };
    const response = await authedFetch(`${BACKEND_URI}/history/unpin`, {
        method: "PATCH",
        headers: JSON_HEADERS,
        body: JSON.stringify(payload)
    });
    if (!response.ok) throw new Error(`Failed to unpin conversation: ${response.statusText}`);
    return response.json();
}
